// src/hooks/useAnalyticsWithConsent.ts
import { useEffect } from 'react';
import { analyticsService, AnalyticsEvents, ValidEventName } from '../analytics/firebaseAnalytics';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { getAnalytics } from 'firebase/analytics';
import { User } from 'firebase/auth';
import { useCookieConsent } from '../context/CookieConsentContext';
import { isPixelInitialized } from '../utility/FacebookPixel';

// Enhanced interfaces
interface PropertyData {
  type: string;
  price: number;
  location: string;
  images?: string[];
  amenities?: string[];
  virtualTourUrl?: string;
  [key: string]: any;
}

interface PropertyEngagement {
  viewDuration: number;
  scrollDepth: number;
  interactionCount: number;
  inquirySent: boolean;
  sectionViewTimes?: Record<string, number>;
}

interface SearchParams {
  query?: string;
  filters?: Record<string, any>;
  resultsCount?: number;
  [key: string]: any;
}

interface ErrorContext {
  component?: string;
  action?: string;
  timestamp?: string;
  [key: string]: any;
}

interface PerformanceMetrics {
  loadTime: number;
  renderTime: number;
  resourcesLoaded: number;
  timeToInteractive?: number;
}

// Add Facebook Pixel specific interfaces
interface PixelViewContentParams {
  content_name?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}

interface PixelSearchParams {
  search_string?: string;
  content_category?: string;
  content_ids?: string[];
  content_type?: string;
  value?: number;
  currency?: string;
}

interface PixelPurchaseParams {
  value: number;
  currency: string;
  content_ids?: string[];
  content_type?: string;
  transaction_id?: string;
}

// Reddit Pixel customer match keys
interface RedditCustomerMatchKeys {
  email?: string;
  phoneNumber?: string;
  externalId?: string;
  idfa?: string; // iOS Identifier for Advertisers
  aaid?: string; // Android Advertising ID
}

// Reddit Pixel event metadata
interface RedditEventMetadata extends RedditCustomerMatchKeys {
  itemCount?: number;
  value?: number;
  currency?: string;
  conversionId?: string;
  [key: string]: any;
}

export interface AnalyticsHookReturn {
  // Consent management methods
  grantPixelConsent: () => void;
  revokePixelConsent: () => void;
  // Add Facebook Pixel & Reddit Pixel specific methods
  trackPixelPageView: (redditMetadata?: RedditEventMetadata) => void;
  trackPixelViewContent: (
    params: PixelViewContentParams,
    redditMetadata?: RedditEventMetadata
  ) => void;
  trackPixelSearch: (params: PixelSearchParams, redditMetadata?: RedditEventMetadata) => void;
  trackPixelPurchase: (params: PixelPurchaseParams, redditMetadata?: RedditEventMetadata) => void;
  trackPixelCustom: (
    eventName: string,
    params?: Record<string, any>,
    redditMetadata?: RedditEventMetadata
  ) => void;

  // Custom analytics events (allows events outside ValidEventName union)
  trackCustomEvent: (eventName: string, params?: Record<string, any>) => void;

  logEvent: (eventName: ValidEventName, params?: Record<string, any>) => void;
  trackPageView: (pageName: string, params?: Record<string, any>) => void;
  trackUserAction: (action: string, category: string, label?: string, value?: number) => void;
  trackPropertyView: (propertyId: number, propertyData: PropertyData) => void;
  trackPropertySearch: (searchParams: SearchParams) => void;
  trackError: (errorCode: string, errorMessage: string, errorContext?: ErrorContext) => void;
  initializeUser: (user: User) => void;
  // New tracking methods
  trackPropertyEngagement: (propertyId: number, engagement: PropertyEngagement) => void;
  trackContentEffectiveness: (propertyId: number, contentMetrics: Record<string, any>) => void;
  trackPerformance: (pageId: string, metrics: PerformanceMetrics) => void;
  trackInteraction: (interactionType: string, context: Record<string, any>) => void;
  isEnabled: boolean;
}

export const useAnalyticsWithConsent = (): AnalyticsHookReturn => {
  const { hasAnalyticsConsent } = useCookieConsent();
  const analytics = analyticsService;
  const isEnabled = hasAnalyticsConsent();

  useEffect(() => {
    try {
      const analyticsInstance = getAnalytics();
      setAnalyticsCollectionEnabled(analyticsInstance, isEnabled);
    } catch (error) {
      console.error('Error configuring analytics:', error);
    }
  }, [isEnabled]);

  const safelyTrack = <T extends any[]>(trackingFunction: (...args: T) => void, ...args: T) => {
    if (isEnabled) {
      try {
        trackingFunction.apply(analytics, args);
      } catch (error) {
        console.error('Error tracking analytics:', error);
      }
    }
  };

  // Facebook Pixel tracking wrapper
  const safelyTrackPixel = (eventName: string, trackCall: () => void) => {
    const initialized = isPixelInitialized();

    if (isEnabled && initialized) {
      try {
        if (window.fbq) {
          trackCall();
        }
      } catch (error) {}
    }
  };

  // Reddit Pixel tracking wrapper
  const safelyTrackReddit = (eventName: string, trackCall: () => void) => {
    if (isEnabled) {
      try {
        if (window.rdt) {
          trackCall();
        }
      } catch (error) {
        // Silent fail for Reddit tracking
      }
    }
  };

  return {
    // Facebook Pixel & Reddit Pixel tracking methods
    trackPixelPageView: (redditMetadata?: RedditEventMetadata) => {
      safelyTrackPixel('PageView', () => window.fbq?.('track', 'PageView'));
      safelyTrackReddit('PageVisit', () => window.rdt?.('track', 'PageVisit', redditMetadata));
    },

    trackPixelViewContent: (
      params: PixelViewContentParams,
      redditMetadata?: RedditEventMetadata
    ) => {
      safelyTrackPixel('ViewContent', () =>
        window.fbq?.('track', 'ViewContent', {
          ...params,
        })
      );
      safelyTrackReddit('ViewContent', () =>
        window.rdt?.('track', 'ViewContent', {
          ...redditMetadata,
        })
      );
    },

    trackPixelSearch: (params: PixelSearchParams, redditMetadata?: RedditEventMetadata) => {
      safelyTrackPixel('Search', () =>
        window.fbq?.('track', 'Search', {
          ...params,
        })
      );
      safelyTrackReddit('Search', () =>
        window.rdt?.('track', 'Search', {
          ...redditMetadata,
        })
      );
    },

    trackPixelPurchase: (params: PixelPurchaseParams, redditMetadata?: RedditEventMetadata) => {
      safelyTrackPixel('Purchase', () =>
        window.fbq?.('track', 'Purchase', {
          ...params,
        })
      );
      safelyTrackReddit('Purchase', () =>
        window.rdt?.('track', 'Purchase', {
          value: params.value,
          ...redditMetadata,
        })
      );
    },

    trackPixelCustom: (
      eventName: string,
      params?: Record<string, any>,
      redditMetadata?: RedditEventMetadata
    ) => {
      safelyTrackPixel(`Custom_${eventName}`, () => window.fbq?.('trackCustom', eventName, params));
      safelyTrackReddit(`Custom_${eventName}`, () =>
        window.rdt?.('track', 'Custom', {
          customEventName: eventName,
          ...redditMetadata,
        })
      );
    },

    // Custom analytics events helper
    trackCustomEvent: (eventName: string, params?: Record<string, any>) => {
      safelyTrack(analytics.logEvent.bind(analytics), eventName as ValidEventName, params);
    },
    // Consent management methods
    grantPixelConsent: () => {
      if (isEnabled && isPixelInitialized()) {
        try {
          if (window.fbq) {
            window.fbq('consent', 'grant');
            console.debug('Facebook Pixel consent granted');
          }
        } catch (error) {
          console.error('Error granting Facebook Pixel consent:', error);
        }
      }
    },

    revokePixelConsent: () => {
      if (isPixelInitialized()) {
        try {
          if (window.fbq) {
            window.fbq('consent', 'revoke');
            console.debug('Facebook Pixel consent revoked');
          }
        } catch (error) {
          console.error('Error revoking Facebook Pixel consent:', error);
        }
      }
    },
    // Existing methods
    logEvent: (eventName, params) =>
      safelyTrack(analytics.logEvent.bind(analytics), eventName, params),

    trackPageView: (pageName, params) =>
      safelyTrack(analytics.trackPageView.bind(analytics), pageName, params),

    trackUserAction: (action, category, label, value) =>
      safelyTrack(analytics.trackUserAction.bind(analytics), action, category, label, value),

    trackPropertyView: (propertyId, propertyData) =>
      safelyTrack(analytics.trackPropertyView.bind(analytics), propertyId, {
        ...propertyData,
        timestamp: new Date().toISOString(),
        price_range: getPriceRange(propertyData.price),
        has_virtual_tour: !!propertyData.virtualTourUrl,
        image_count: propertyData.images?.length || 0,
        amenities_count: propertyData.amenities?.length || 0,
      }),

    trackPropertySearch: (searchParams) =>
      safelyTrack(analytics.trackPropertySearch.bind(analytics), {
        ...searchParams,
        timestamp: new Date().toISOString(),
      }),

    trackError: (errorCode, errorMessage, errorContext) =>
      safelyTrack(analytics.trackError.bind(analytics), errorCode, errorMessage, {
        ...errorContext,
        timestamp: new Date().toISOString(),
      }),

    initializeUser: (user) => safelyTrack(analytics.initializeUser.bind(analytics), user),

    // New enhanced strategies tracking methods for platform improvements
    trackPropertyEngagement: (propertyId, engagement) =>
      safelyTrack(analytics.logEvent.bind(analytics), AnalyticsEvents.PROPERTY_ENGAGEMENT, {
        property_id: propertyId,
        ...engagement,
        timestamp: new Date().toISOString(),
      }),

    trackContentEffectiveness: (propertyId, contentMetrics) =>
      safelyTrack(analytics.logEvent.bind(analytics), AnalyticsEvents.CONTENT_ENGAGEMENT, {
        property_id: propertyId,
        ...contentMetrics,
        timestamp: new Date().toISOString(),
      }),

    trackPerformance: (pageId, metrics) =>
      safelyTrack(analytics.logEvent.bind(analytics), AnalyticsEvents.PERFORMANCE_METRICS, {
        page_id: pageId,
        ...metrics,
        timestamp: new Date().toISOString(),
      }),

    trackInteraction: (interactionType, context) =>
      safelyTrack(analytics.logEvent.bind(analytics), AnalyticsEvents.USER_INTERACTION, {
        interaction_type: interactionType,
        ...context,
        timestamp: new Date().toISOString(),
      }),

    isEnabled,
  };
};

// Helper functions
const getPriceRange = (price: number): string => {
  if (price < 1000) return 'low';
  if (price < 2000) return 'medium';
  return 'high';
};
